const enc = new TextEncoder();
const dec = new TextDecoder();

export const utf8 = (s: string): Uint8Array => enc.encode(s);
export const utf8Decode = (b: Uint8Array): string => dec.decode(b);

export function concat(...parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i]! ^ b[i]!;
  return d === 0;
}

export const toHex = (b: Uint8Array): string => b.toHex();
export const fromHex = (s: string): Uint8Array => Uint8Array.fromHex(s.replace(/[\s:]/g, ''));

export const toBase64 = (b: Uint8Array): string => b.toBase64();
export const toBase64Url = (b: Uint8Array): string => b.toBase64({ alphabet: 'base64url', omitPadding: true });

/** Decodes standard or URL-safe base64, tolerating whitespace and missing padding. */
export function fromBase64(s: string): Uint8Array {
  let t = s.replace(/\s+/g, '');
  const url = /[-_]/.test(t);
  if (url) t = t.replace(/-/g, '+').replace(/_/g, '/');
  while (t.length % 4) t += '=';
  return Uint8Array.fromBase64(t);
}

/** Formats bytes as colon-separated uppercase hex pairs (fingerprint style). */
export function toColonHex(b: Uint8Array): string {
  return toHex(b).toUpperCase().match(/../g)?.join(':') ?? '';
}

export interface PemBlock {
  label: string;
  der: Uint8Array;
}

const PEM_RE = /-----BEGIN ([^-]+)-----([\s\S]*?)-----END \1-----/g;

/** Extracts every PEM block from a text; also accepts bare base64 or a single DER-as-hex blob. */
export function pemDecodeAll(text: string): PemBlock[] {
  const out: PemBlock[] = [];
  for (const m of text.matchAll(PEM_RE)) {
    out.push({ label: m[1]!.trim(), der: fromBase64(m[2]!) });
  }
  if (out.length === 0) {
    const t = text.trim();
    if (t.length > 0) {
      if (/^[0-9a-fA-F\s:]+$/.test(t) && t.replace(/[\s:]/g, '').length % 2 === 0) {
        out.push({ label: 'HEX', der: fromHex(t) });
      } else if (/^[A-Za-z0-9+/=\s_-]+$/.test(t)) {
        out.push({ label: 'BASE64', der: fromBase64(t) });
      }
    }
  }
  return out;
}

export function pemEncode(der: Uint8Array, label = 'CERTIFICATE'): string {
  const b64 = toBase64(der).match(/.{1,64}/g)?.join('\n') ?? '';
  return `-----BEGIN ${label}-----\n${b64}\n-----END ${label}-----\n`;
}
